// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
    baseUrl: `${string}://${string}` | (string & {});
};

/**
 * AnalysisRequest
 *
 * Request to analyze a score.
 *
 * Inhaltsbasiert (stateless): ``music_xml`` ist der zu analysierende Inhalt. ``file_id`` ist
 * nur ein opakes Label (Abwärtskompatibilität); ohne ``music_xml`` schlägt die Analyse fehl.
 */
export type AnalysisRequest = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Music Xml
     *
     * Der zu analysierende MusicXML-Inhalt (inhaltsbasiert, stateless).
     */
    music_xml?: string | null;
};

/**
 * AnalysisResult
 *
 * Music file analysis result.
 *
 * Enthält bewusst KEIN ``music_xml``: bei ``POST /api/analyze`` hätte es exakt den
 * Request-Body gespiegelt (Antwortgröße x2), und bei den Transform-Endpunkten liegt der
 * Inhalt im Top-Level-Feld ``FileUploadResponse.music_xml``.
 */
export type AnalysisResult = {
    /**
     * Voices
     */
    voices?: number | null;
    /**
     * Is Polyphonic
     *
     * True, wenn das Stück echt mehrstimmig ist (mehrere Parts/Voices, Akkorde oder simultane Onsets).
     */
    is_polyphonic?: boolean;
    /**
     * Key
     */
    key?: string | null;
    /**
     * Time Signature
     */
    time_signature?: string | null;
    /**
     * Num Measures
     */
    num_measures?: number;
    /**
     * Num Notes
     */
    num_notes?: number;
    /**
     * Parts
     */
    parts?: Array<PartInfo>;
    /**
     * Unmappable Notes
     */
    unmappable_notes?: Array<string>;
    /**
     * Unmappable Count
     */
    unmappable_count?: number;
    /**
     * Unmappable Occurrences
     */
    unmappable_occurrences?: {
        [key: string]: number;
    };
    /**
     * Unmappable Occurrences Count
     */
    unmappable_occurrences_count?: number;
    /**
     * Has Original Chord Symbols
     *
     * True, wenn das Stück ChordSymbols enthält, die NICHT als automatisch erkannt markiert sind (d. h. original/vom User gesetzt). Steuert im Frontend den Hinweis, dass die Akkorderkennung nicht nötig ist.
     */
    has_original_chord_symbols?: boolean;
    /**
     * Error
     */
    error?: string | null;
};

/**
 * AssembleRequest
 *
 * Request to assemble + export a final score from a client-side per-measure selection.
 */
export type AssembleRequest = {
    /**
     * File Id
     */
    file_id: string;
    settings?: ConversionSettings;
    /**
     * Original Key
     */
    original_key?: string | null;
    /**
     * Per Measure Selection
     *
     * One internal variant name per measure
     */
    per_measure_selection?: Array<string>;
    /**
     * Conversion Id
     */
    conversion_id?: string | null;
    /**
     * Original Filename
     *
     * Anzeigename für den Titel des Ergebnis-Scores (optional).
     */
    original_filename?: string | null;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt (inhaltsbasiert, stateless).
     */
    music_xml: string;
};

/**
 * BassButton
 *
 * Ein Knopf der Bass-Seite.
 *
 * ``griffschrift`` ist der in der Bass-Zeile gedruckte Name – frei vergebbar, weil die
 * Benennungssysteme (Rosenzopf, Derschmidt, Pauli, Michlbauer) sich unterscheiden.
 *
 * ``druck``/``zug`` beschreiben, was der Knopf in der jeweiligen Balgrichtung erzeugt:
 * bei ``kind="bass"`` ein Tonname (``"G"``, ``"B-"``; deutsche Eingaben wie ``"H"``/``"Es"``
 * werden akzeptiert), bei ``kind="chord"`` eine Akkord-Figure für ``harmony.ChordSymbol``
 * (``"G"``, ``"Dm"``). ``None`` = der Knopf klingt in dieser Richtung nicht.
 */
export type BassButton = {
    /**
     * Griffschrift
     */
    griffschrift: string;
    kind?: KindEnum;
    /**
     * Druck
     */
    druck?: string | null;
    /**
     * Zug
     */
    zug?: string | null;
};

/**
 * BassButtonTonesRequest
 *
 * Request für ``POST /api/bass-button-tones``: nur die Bass-Belegung.
 */
export type BassButtonTonesRequest = {
    /**
     * Die Bass-Belegung des Instruments (Harmonika-Tab).
     */
    bass_layout?: BassLayoutSettings;
};

/**
 * BassButtonTonesResponse
 *
 * Was jeder Bassknopf klingt – MIDI je Knopf, angeordnet wie das Layout (Reihe → Knopf).
 */
export type BassButtonTonesResponse = {
    /**
     * Druck
     *
     * Klingende MIDI-Töne je Knopf auf Druck; leer = klingt nicht.
     */
    druck: Array<Array<Array<number>>>;
    /**
     * Zug
     *
     * Klingende MIDI-Töne je Knopf auf Zug; leer = klingt nicht.
     */
    zug: Array<Array<Array<number>>>;
};

/**
 * BassLayoutSettings
 *
 * Die vollständige Bass-Seite eines Instruments.
 */
export type BassLayoutSettings = {
    /**
     * Name
     */
    name?: string;
    /**
     * Rows
     */
    rows?: Array<BassRow>;
};

/**
 * BassMeasureOverride
 *
 * Ein handbearbeiteter Bass-Takt, wie er in die Griffschrift übernommen wird.
 */
export type BassMeasureOverride = {
    /**
     * Slots
     */
    slots: Array<BassOverrideSlot>;
    /**
     * Druck
     */
    druck?: boolean | null;
};

/**
 * BassOverrideSlot
 *
 * Eine vom Nutzer geschriebene Bass-Position: wo, wie lange, welche Töne.
 *
 * ``pitches`` in music21-Schreibweise mit Oktave; ein Eintrag = Einzelton (Grundbass), mehrere =
 * Akkord. Die **Silbe** wird bewusst NICHT hier festgehalten, sondern erst bei der Konvertierung
 * aus der dann feststehenden Balgrichtung abgeleitet.
 */
export type BassOverrideSlot = {
    /**
     * Offset
     */
    offset: number;
    /**
     * End
     */
    end: number;
    /**
     * Pitches
     */
    pitches: Array<string>;
};

/**
 * BassPreviewRequest
 *
 * Request to add or remove the sounding bass preview part (eigenständiger Input-Schritt).
 *
 * Der Part bildet den Wechselbass ab, den die Harmonika zu den Akkordsymbolen spielen würde –
 * damit prüfbar ist, ob die Akkorderkennung stimmt, BEVOR konvertiert wird. Er ist reine
 * Prüfhilfe: Konvertierung und Analyse klammern ihn aus.
 */
export type BassPreviewRequest = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Die Bass-Belegung des Instruments (Harmonika-Tab). Bewusst nur das Layout statt der kompletten ConversionSettings – jeder Input-Schritt bekommt sein eigenes Settings-Modell.
     */
    layout?: BassLayoutSettings;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, auf den der Schritt angewendet wird (inhaltsbasiert, reihenfolgeunabhängig).
     */
    music_xml: string;
    /**
     * Remove
     *
     * True entfernt einen vorhandenen Vorschau-Part, statt ihn zu erzeugen.
     */
    remove?: boolean;
    /**
     * Measure Styles
     *
     * Spielweise je Takt: {"<takt-index>": "wechselbass"|"akkordtoene"|"lauf:<name>"}, Index 0-basiert. **None (Feld fehlt) behält die im Score vermerkten Spielweisen** – ein leeres Objekt löscht sie dagegen. Der Vermerk reist in den Score-Metadaten mit der Datei.
     */
    measure_styles?: {
        [key: string]: string;
    } | null;
};

/**
 * BassRow
 *
 * Eine Knopfreihe der Bass-Seite (äußere Reihe, innere Reihe, …).
 */
export type BassRow = {
    /**
     * Name
     */
    name: string;
    /**
     * Buttons
     */
    buttons?: Array<BassButton>;
};

/**
 * BeamSearchSettings
 */
export type BeamSearchSettings = {
    /**
     * Use Beam Search
     */
    use_beam_search?: boolean;
    /**
     * Beam Width
     */
    beam_width?: number;
    /**
     * Enable Dubug
     */
    enable_dubug?: boolean;
    /**
     * Transition Penalty
     */
    transition_penalty?: number;
    /**
     * Balance Druck Zug
     */
    balance_druck_zug?: boolean;
    /**
     * Max Druck Zug Ratio
     */
    max_druck_zug_ratio?: number | null;
};

/**
 * Body_upload_file_api_upload_post
 */
export type BodyUploadFileApiUploadPost = {
    /**
     * File
     */
    file: string;
    /**
     * Chord Inference
     */
    chord_inference?: string | null;
    /**
     * Bass Layout
     */
    bass_layout?: string | null;
};

/**
 * Body_upload_pdf_api_upload_pdf_post
 */
export type BodyUploadPdfApiUploadPdfPost = {
    /**
     * File
     */
    file: string;
    /**
     * Improve Image
     */
    improve_image?: boolean;
    /**
     * Remove Bass Parts
     */
    remove_bass_parts?: boolean;
    /**
     * Chord Inference
     */
    chord_inference?: string | null;
    /**
     * Bass Layout
     */
    bass_layout?: string | null;
};

/**
 * ChordDirectionResult
 *
 * Griffe und Bass eines Akkords in einer Balgrichtung.
 */
export type ChordDirectionResult = {
    /**
     * Grips
     *
     * Griffe, bester zuerst.
     */
    grips?: Array<Grip>;
    /**
     * Bass Syllables
     *
     * Wechselbass-Silben (Grund, Akkord, Gegen, Akkord).
     */
    bass_syllables?: Array<string>;
    /**
     * Bass Midi
     *
     * Klingende MIDI-Töne je Bass-Slot (leer = Lücke).
     */
    bass_midi?: Array<Array<number>>;
    /**
     * Has Chord Button
     *
     * False, wenn die Bass-Seite in dieser Richtung keinen Akkordknopf dafür hat (dann klingen die Akkordtöne einzeln).
     */
    has_chord_button?: boolean;
};

/**
 * ChordGripsRequest
 *
 * Request the grips of a chord (Grundton + Akkordtyp) – der Akkord-Finder der Webapp.
 *
 * Unabhängig von einer Partitur. ``stimmung_offset`` rechnet eine andere Harmonika-Stimmung
 * oder eine Transposition in den GCFB-Raum der Engine um (Griffschrift ist stimmungsunabhängig).
 */
export type ChordGripsRequest = {
    /**
     * Root Pc
     *
     * Grundton als Tonklasse (0 = C … 11 = H).
     */
    root_pc: number;
    /**
     * Chord Type
     *
     * Akkordtyp: single | dur | moll | dim | aug | dur_7 | dur_maj7 | moll_7 | dim_7 | dim_m7b5 | sus4 | sus4_7 (siehe griffschrift.selection.chord_finder.CHORD_TYPES).
     */
    chord_type: string;
    /**
     * Stimmung Offset
     *
     * Halbtöne, um die die eingestellte Stimmung über G-C-F-B liegt (A-D-G-C = 2). Der gegriffene Grundton ist root_pc − stimmung_offset.
     */
    stimmung_offset?: number;
    /**
     * Die Bass-Belegung des Instruments (Harmonika-Tab).
     */
    bass_layout?: BassLayoutSettings;
};

/**
 * ChordGripsResponse
 *
 * Griffe eines Akkords auf Druck und Zug, samt Griffschrift-Schnipsel.
 */
export type ChordGripsResponse = {
    /**
     * Figure
     *
     * Der gesuchte Akkord in music21-Schreibweise (z. B. 'D', 'F#m7').
     */
    figure: string;
    /**
     * Griff Figure
     *
     * Der gegriffene Akkord im GCFB-Raum (nach stimmung_offset).
     */
    griff_figure: string;
    /**
     * Griff Root Pc
     *
     * Gegriffener Grundton als Tonklasse.
     */
    griff_root_pc: number;
    /**
     * Tones
     *
     * Tonklassen des gegriffenen Akkords ab Grundton.
     */
    tones: Array<number>;
    /**
     * Music Xml
     *
     * Griffschrift: ein Takt je Griff, erst alle Druck-, dann alle Zug-Griffe.
     */
    music_xml: string;
    druck: ChordDirectionResult;
    zug: ChordDirectionResult;
};

/**
 * ChordInferenceRequest
 *
 * Request to (re-)apply chord-symbol detection to a file (eigenständiger Input-Schritt).
 *
 * Entfernt zunächst alle zuvor automatisch erkannten (markierten) ChordSymbols und erkennt
 * dann mit den übergebenen Settings neu. Originale/vom User gesetzte Symbole bleiben erhalten –
 * außer ``settings.overwrite_existing`` ist gesetzt (dann werden alle Symbole ersetzt).
 */
export type ChordInferenceRequest = {
    /**
     * File Id
     */
    file_id: string;
    settings: ChordInferenceSettings;
    /**
     * Die Bass-Belegung des Instruments (Harmonika-Tab). Bei ``settings.restrict_to_bass_layout`` werden nur Akkorde erkannt, die hier einen Akkordknopf haben.
     */
    layout?: BassLayoutSettings;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, auf den die Akkorderkennung angewendet wird (inhaltsbasiert, reihenfolgeunabhängig). Zuvor automatisch erkannte Akkorde werden ersetzt; originale Symbole bleiben unangetastet, sofern nicht ``settings.overwrite_existing`` gesetzt ist.
     */
    music_xml: string;
};

/**
 * ChordInferenceSettings
 *
 * Settings für die Folk-Akkordsymbol-Erkennung aus gespielten Pitches.
 *
 * Fehlen in einem Takt Akkordsymbole, wird nach dem Upload (bzw. als idempotenter Fallback in
 * der Konvertierungspipeline) je Takt ein Akkordsymbol – bei Wechsel mitten im Takt auch
 * mehrere – aus den klingenden Tönen abgeleitet und
 * in den Melodie-Part eingefügt – damit die akkordbasierte Druck/Zug-Präferenz und die
 * Bass-Lyrics einen Anhaltspunkt haben. Takte mit vorhandenem (originalem) Symbol bleiben
 * unangetastet. Die eigentliche Erkennung leistet die volksmusik-getunte Engine
 * ``griffschrift.chords.folk_chord_detection``; ``griffschrift.chords.inference`` adaptiert sie
 * an diesen Kontrakt (Marker/Idempotenz/Lückenfüllung).
 *
 * Regler (direkt an ``FolkChordAnalysisConfig`` durchgereicht):
 * - ``min_chord_confidence``     – Score-Schwelle (0..1); höher = weniger, sicherere Akkorde.
 * - ``non_chord_tone_tolerance`` – Anteil des Maximalgewichts, ab dem eine Tonklasse als
 * akkordrelevant statt Durchgangston gilt (0..1); höher = mehr Töne fließen ein.
 * - ``max_pitch_classes``        – max. Tonklassen, die je Fenster berücksichtigt werden.
 * - ``merge_repeated_symbols``   – nur bei Akkordwechsel ein Symbol setzen (Default an).
 * - ``detect_changes_within_measure`` – Akkordwechsel auf Schlaggrenzen erkennen (Default an);
 * ein neu einsetzender Akkord muss dafür **ganz** klingen (``FolkChordAnalysisConfig.split_by_beat``,
 * Regel in ``folk_chord_detection._detect_measure_segments``).
 * - ``restrict_to_bass_layout``  – nur Akkorde vorschlagen, für die das Bass-Layout
 * (``ConversionSettings.BassLayout`` bzw. das Layout des Requests) einen Akkordknopf hat, plus
 * deren Zwischendominanten (Default an; ``folk_chord_detection._restrict_to_allowed_chords``).
 * - ``key_override``             – feste Tonart (``"F"``, ``"B-"``, ``"Dm"``); ``None`` = Analyse.
 * - ``follow_key_changes``       – je Vorzeichenwechsel eine eigene Tonart (Trio), Default an
 * (``folk_chord_detection._key_sections``).
 *
 * ``overwrite_existing`` (Default ``False``, KEIN Folk-Regler): bei ``True`` werden **alle**
 * vorhandenen Akkordsymbole – auch originale/vom User gesetzte – vor der Erkennung entfernt und
 * durch die Erkennung ersetzt. Das Backend honoriert das Feld **überall**, wo die Settings
 * angewendet werden: im Input-Schritt (``apply_chord_inference_input_step`` –
 * ``POST /api/chord-inference`` und Upload mit mitgeschicktem ``chord_inference``-Formfeld)
 * und im Pipeline-Fallback der Konvertierung. Es gibt bewusst keine Backend-Ausnahmen
 * („Backend ignoriert nie") – wo ein Überschreiben nicht gewollt ist, **pinnt das Frontend**
 * den Wert (Convert-Requests und Upload senden fest ``overwrite_existing: false``; nur die
 * per-Aktion-Checkbox am chord-inference-Endpunkt setzt ``true``).
 */
export type ChordInferenceSettings = {
    /**
     * Enabled
     */
    enabled?: boolean;
    /**
     * Min Chord Confidence
     */
    min_chord_confidence?: number;
    /**
     * Non Chord Tone Tolerance
     */
    non_chord_tone_tolerance?: number;
    /**
     * Max Pitch Classes
     */
    max_pitch_classes?: number;
    /**
     * Merge Repeated Symbols
     */
    merge_repeated_symbols?: boolean;
    /**
     * Detect Changes Within Measure
     */
    detect_changes_within_measure?: boolean;
    /**
     * Restrict To Bass Layout
     */
    restrict_to_bass_layout?: boolean;
    /**
     * Key Override
     */
    key_override?: string | null;
    /**
     * Follow Key Changes
     */
    follow_key_changes?: boolean;
    /**
     * Overwrite Existing
     */
    overwrite_existing?: boolean;
};

/**
 * ConversionMode
 *
 * Conversion mode options.
 */
export enum ConversionMode {
    Global = 'global',
    Auto = 'auto',
    PerMeasure = 'per_measure'
}

/**
 * ConversionRequest
 *
 * Request to convert a file to Griffschrift (inhaltsbasiert, stateless).
 */
export type ConversionRequest = {
    /**
     * File Id
     */
    file_id: string;
    mode?: ConversionMode;
    settings?: ConversionSettings;
    /**
     * Original Key
     */
    original_key?: string | null;
    /**
     * Conversion Id
     */
    conversion_id?: string | null;
    /**
     * Original Filename
     *
     * Anzeigename für den Titel des Ergebnis-Scores (optional).
     */
    original_filename?: string | null;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, der konvertiert wird (inhaltsbasiert).
     */
    music_xml: string;
};

/**
 * ConversionSettings
 */
export type ConversionSettings = {
    /**
     * Reduce Octave
     */
    REDUCE_OCTAVE?: number | null;
    /**
     * Max Alternatives Druck
     */
    MAX_ALTERNATIVES_DRUCK?: number;
    /**
     * Max Alternatives Zug
     */
    MAX_ALTERNATIVES_ZUG?: number;
    /**
     * Debug Output
     */
    DEBUG_OUTPUT?: boolean;
    /**
     * Addmeasurements
     */
    AddMeasurements?: boolean;
    /**
     * Fixtakt
     */
    FixTakt?: boolean;
    /**
     * Append
     */
    Append?: boolean;
    /**
     * Enforce C Major
     */
    ENFORCE_C_MAJOR?: boolean;
    /**
     * Basekey
     */
    BaseKey?: string;
    /**
     * Basekeymode
     */
    BaseKeyMode?: string;
    Lyrics?: LyricSettings;
    Beam?: BeamSearchSettings;
    ChordInference?: ChordInferenceSettings;
    BassLayout?: BassLayoutSettings;
    /**
     * Bassoverrides
     *
     * Von Hand geschriebene Takte des Bass-Vorschau-Systems, nach Takt-Index (0-basiert). **Server-owned wie BaseKey/BaseKeyMode**: `convert_piece` liest sie aus dem Vorschau-Part, bevor es ihn entfernt, und überschreibt damit jeden Wert aus dem Request. Sie bestimmen die Bass-Zeile dieser Takte und ziehen die Balgrichtung auf ihre Seite (siehe `mapping/bass_preview.py`).
     */
    BassOverrides?: {
        [key: string]: BassMeasureOverride;
    };
    /**
     * Selected Parts
     */
    selected_parts?: Array<number>;
};

/**
 * ConversionStatus
 *
 * Conversion status options.
 */
export enum ConversionStatus {
    Pending = 'pending',
    Processing = 'processing',
    Completed = 'completed',
    Error = 'error'
}

/**
 * ConversionStatusStore
 *
 * Result of a (synchronous) conversion.
 *
 * Stateless: das fertige Ergebnis (``music_xml``) kommt inline in der Convert-Antwort. Es gibt
 * keinen serverseitigen Status-Speicher und keine ``preview_url``/``download_urls`` mehr;
 * Downloads erzeugt der Client via ``POST /api/render`` / ``POST /api/midi`` aus ``music_xml``.
 */
export type ConversionStatusStore = {
    status: ConversionStatus;
    /**
     * Conversion Id
     */
    conversion_id?: string | null;
    /**
     * Music Xml
     */
    music_xml?: string | null;
    /**
     * Message
     */
    message?: string | null;
    /**
     * File Id
     */
    file_id?: string | null;
    /**
     * Error
     */
    error?: string | null;
    /**
     * Original Key
     */
    original_key?: string | null;
};

/**
 * ConvertVariantsRequest
 *
 * Request to compute all per-measure Griffschrift variants at once (no selection yet).
 */
export type ConvertVariantsRequest = {
    /**
     * File Id
     */
    file_id: string;
    settings?: ConversionSettings;
    /**
     * Original Key
     */
    original_key?: string | null;
    /**
     * Original Filename
     *
     * Anzeigename für den Titel des Ergebnis-Scores (optional).
     */
    original_filename?: string | null;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt (inhaltsbasiert, stateless).
     */
    music_xml: string;
};

/**
 * ConvertVariantsResponse
 *
 * All per-measure variants plus a recommended default selection for client-side assembly.
 */
export type ConvertVariantsResponse = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Variant Names
     *
     * All internal variant names, in canonical order
     */
    variant_names?: Array<string>;
    /**
     * Variant Labels
     *
     * Internal name -> display label
     */
    variant_labels?: {
        [key: string]: string;
    };
    /**
     * Num Measures
     */
    num_measures?: number;
    /**
     * Measures
     */
    measures?: Array<MeasureVariants>;
    /**
     * Default Selection
     *
     * Recommended variant name per measure (auto/beam search)
     */
    default_selection?: Array<string>;
    /**
     * Original Key
     */
    original_key?: string | null;
};

/**
 * DiatonicShiftRequest
 *
 * Request to apply diatonic shift to a file.
 */
export type DiatonicShiftRequest = {
    /**
     * File Id
     */
    file_id: string;
    settings: DiatonicShiftSettings;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, auf den der Shift angewendet wird. Damit ist die Operation unabhängig von der Reihenfolge (Transpose/Shift/Edit) immer auf dem aktuellen Stand korrekt.
     */
    music_xml: string;
};

/**
 * DiatonicShiftSettings
 *
 * Settings for diatonic note/interval transformations.
 *
 * ``operation`` wählt den Eingriff:
 *
 * - ``add_shifted_notes``    – zu jeder Note den um ``interval`` diatonisch verschobenen Ton
 * als Akkordton hinzufügen (einziger Modus, der ``interval`` braucht).
 * - ``reduce_triad_to_dyad`` – Dreiklang auf Zweiklang reduzieren, gesteuert über
 * ``triad_reduction`` (welche **zwei** Töne bleiben); wirkt nur bei genau drei Tönen.
 * - ``remove_highest_note`` / ``remove_lowest_note`` – den höchsten bzw. tiefsten klingenden
 * Ton entfernen, **größenunabhängig** (jeder Akkord ab zwei Tönen).
 */
export type DiatonicShiftSettings = {
    /**
     * Enabled
     */
    enabled?: boolean;
    operation?: OperationEnum;
    /**
     * Interval
     */
    interval?: number | null;
    triad_reduction?: TriadReductionEnum;
};

/**
 * FileUploadResponse
 *
 * Response after an upload/transform step.
 *
 * Stateless: ``music_xml`` enthält den vollständigen (verarbeiteten) Inhalt inline. Es gibt
 * KEINE serverseitigen Download-/MIDI-URLs mehr – Downloads laufen clientseitig (Blob aus
 * ``music_xml``) bzw. über den stateless ``POST /api/render`` / ``POST /api/midi``. Die
 * ``file_id`` ist nur noch ein opakes Client-Label (keine Server-I/O, kein ``uploads/``-State).
 */
export type FileUploadResponse = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Filename
     */
    filename: string;
    /**
     * File Size
     */
    file_size?: number;
    /**
     * File Type
     */
    file_type?: string;
    /**
     * Status
     */
    status?: string;
    /**
     * Music Xml
     */
    music_xml?: string | null;
    /**
     * Message
     */
    message?: string | null;
    /**
     * Optionales, inline mitgeliefertes Analyse-Ergebnis (Tonart/Spielbarkeit/Parts). Von den Transform-Endpunkten (``POST /api/transpose``, ``/api/mode-change``, ``/api/diatonic-shift``, ``/api/merge-voices``, ``/api/bass-part``, ``/api/rebar``) befüllt, damit das Frontend die Analyse atomar aus der Response übernimmt statt eines separaten ``POST /api/analyze``-Aufrufs. ``music_xml`` ist darin redundant und wird auf ``None`` gesetzt (der Inhalt liegt im Top-Level-Feld). Bei allen übrigen Endpunkten ``None``.
     */
    analysis?: AnalysisResult | null;
};

/**
 * Grip
 *
 * A playable grip = a set of simultaneously pressed buttons (one bellows direction).
 */
export type Grip = {
    /**
     * Buttons
     */
    buttons?: Array<GripButton>;
    /**
     * Is Druck
     *
     * Balgrichtung des gesamten Griffs (True=Druck).
     */
    is_druck: boolean;
    /**
     * Score
     *
     * Ergonomie-Score (niedriger = besser).
     */
    score: number;
    /**
     * Playable
     *
     * False, wenn der Griff nicht spielbar ist.
     */
    playable?: boolean;
    /**
     * Bass Lyric
     *
     * Bass-Silbe, die die angeklickte Note mit diesem Griff tragen muss (seine Balgrichtung). ``None`` = Silbe bleibt stehen (gleiche Richtung wie die Note, Bass-Zeile aus, kein Akkord, Handarbeit); ``''`` = Silbe entfernen.
     */
    bass_lyric?: string | null;
};

/**
 * GripButton
 *
 * A single physical harmonica button within a grip.
 */
export type GripButton = {
    /**
     * Row
     *
     * Reihe (Row.value: 1..4, 5=Zusatz, -1=nicht gefunden).
     */
    row: number;
    /**
     * Number
     *
     * Knopfnummer innerhalb der Reihe (ab 1).
     */
    number: number;
    /**
     * Direction
     *
     * 'push' (Druck) oder 'pull' (Zug).
     */
    direction: string;
    /**
     * Is Druck
     *
     * True = Druck, False = Zug.
     */
    is_druck: boolean;
    /**
     * Note
     *
     * Klingende Tonhöhe des Knopfes (z.B. 'G4').
     */
    note: string;
    /**
     * Btn Info
     *
     * Kompakte Knopf-ID, z.B. 'D3_11'.
     */
    btn_info: string;
    /**
     * Griffschrift Pitch
     *
     * Notations-/Staff-Tonhöhe in der Griffschrift.
     */
    griffschrift_pitch: string;
    /**
     * Notehead
     *
     * Notenkopf-Form (normal|x|diamond|triangle).
     */
    notehead: string;
    /**
     * Color
     *
     * Anzeigefarbe der Note (None/schwarz normal, Gleichton #1f35c2, Zusatz #FF00F0).
     */
    color?: string | null;
};

/**
 * HTTPValidationError
 */
export type HttpValidationError = {
    /**
     * Detail
     */
    detail?: Array<ValidationError>;
};

/**
 * LyricSettings
 *
 * Initialisiert DefaultSettings mit Standard-Werten.
 */
export type LyricSettings = {
    /**
     * Preserve
     */
    Preserve?: boolean;
    /**
     * Debugscore
     */
    DebugScore?: boolean;
    /**
     * Debugscoreauto
     */
    DebugScoreAuto?: boolean;
    /**
     * Note
     */
    Note?: boolean;
    /**
     * Basskey
     */
    BassKey?: boolean;
    /**
     * Bass
     */
    Bass?: boolean;
    /**
     * Measurescore
     */
    MeasureScore?: boolean;
    /**
     * Addtotalscore
     */
    AddTotalScore?: boolean;
};

/**
 * MeasureVariant
 *
 * A single Griffschrift variant for one measure (one bellow-direction option).
 */
export type MeasureVariant = {
    /**
     * Variant Name
     *
     * Internal variant name, e.g. 'Druck', 'Alt. Druck 1'
     */
    variant_name: string;
    /**
     * Label
     *
     * Human readable label for the variant
     */
    label: string;
    /**
     * Direction
     *
     * 'push' (Druck) or 'pull' (Zug)
     */
    direction: string;
    /**
     * Music Xml
     *
     * Standalone MusicXML fragment of this single measure
     */
    music_xml: string;
    /**
     * Score
     *
     * Quality score of this variant (lower is better)
     */
    score: number;
    /**
     * Content Hash
     *
     * Hash of the physical buttons; identical hash => identical variant
     */
    content_hash: string;
    /**
     * Playable
     *
     * False if the measure contains unplayable (red) rests
     */
    playable?: boolean;
};

/**
 * MeasureVariants
 *
 * All available variants for a single measure.
 */
export type MeasureVariants = {
    /**
     * Measure Index
     *
     * 0-based position of the measure
     */
    measure_index: number;
    /**
     * Measure Number
     *
     * Musical measure number (handles pickup measures)
     */
    measure_number: number;
    /**
     * Variants
     */
    variants?: Array<MeasureVariant>;
};

/**
 * MidiFromXmlRequest
 *
 * Request to generate a MIDI file from raw MusicXML content (für die Vorschau-Wiedergabe).
 */
export type MidiFromXmlRequest = {
    /**
     * Music Xml
     */
    music_xml: string;
};

/**
 * ModeChangeRequest
 *
 * Request für den Variantwechsel (Dur↔Moll) mit optionaler chromatischer Verschiebung.
 */
export type ModeChangeRequest = {
    /**
     * File Id
     */
    file_id: string;
    target_mode: TargetModeEnum;
    /**
     * Semitones
     *
     * Chromatische Verschiebung, die NACH dem Stufen-Remap angewendet wird (kombinierte Vorschläge wie 'g Moll → C').
     */
    semitones?: number;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, auf den der Variantwechsel angewendet wird (inhaltsbasiert, stateless).
     */
    music_xml: string;
};

/**
 * NoteGripsRequest
 *
 * Request the harmonica grip(s) for one element of a **Griffschrift OUTPUT** score.
 *
 * Inhaltsbasiert/stateless: der Client schickt die (bereits konvertierte) Griffschrift als
 * ``music_xml`` plus die Position des angeklickten Elements. Der Server ermittelt den aktuell
 * verwendeten Griff sowie alle spielbaren Alternativen (Einzelnote **oder** Akkord).
 */
export type NoteGripsRequest = {
    /**
     * Music Xml
     *
     * Die Griffschrift-Ausgabe (MusicXML) als String.
     */
    music_xml: string;
    /**
     * Measure Number
     *
     * Musikalische Taktnummer des angeklickten Elements.
     */
    measure_number: number;
    /**
     * Measure Index
     *
     * Optionaler 0-basierter Ordinalindex des Takts innerhalb des Parts.
     */
    measure_index?: number | null;
    /**
     * Voice Note Index
     *
     * 0-basierter Index der angeklickten Note **innerhalb ihrer Stimme** – zählt roh über alle <note>-Elemente inkl. Pausen UND je Akkordton einzeln (identisch zur Frontend-Adressierung `NoteInfo.voiceNoteIndex`, siehe musicXmlDom `getVoiceNotes`).
     */
    voice_note_index: number;
    /**
     * Voice
     *
     * Stimmennummer (Frontend `NoteInfo.voice`).
     */
    voice?: number;
    /**
     * Part Index
     *
     * 0-basierter Part-Index (Default Melodie).
     */
    part_index?: number;
};

/**
 * NoteGripsResponse
 *
 * Assigned grip + ranked alternatives for one clicked Griffschrift-output element.
 */
export type NoteGripsResponse = {
    /**
     * Is Chord
     *
     * True, wenn das Element ein Akkord (mehrere Knöpfe) ist.
     */
    is_chord: boolean;
    /**
     * Sounding Notes
     *
     * Klingende Tonhöhen des Elements.
     */
    sounding_notes?: Array<string>;
    /**
     * Aktuell in der Ausgabe verwendeter Griff (falls erkennbar).
     */
    assigned?: Grip | null;
    /**
     * Alternatives
     *
     * Alle spielbaren Griffe, aufsteigend nach Score.
     */
    alternatives?: Array<Grip>;
};

/**
 * PartInfo
 *
 * Information about a score part.
 */
export type PartInfo = {
    /**
     * Index
     */
    index: number;
    /**
     * Name
     */
    name: string;
    /**
     * Instrument
     */
    instrument?: string | null;
    /**
     * Num Measures
     */
    num_measures?: number;
    /**
     * Clef
     *
     * Clef-Vorzeichen des Parts (music21 Clef.sign: 'G'=Violin, 'F'=Bass, 'C'=Alt). Dient im Frontend als Schlüssel-Label für die Part-Auswahl.
     */
    clef?: string | null;
};

/**
 * PdfUploadResponse
 *
 * Response for a (potentially multi-movement) PDF upload.
 *
 * Mehrteilige PDFs erzeugen via OMR mehrere **Movements**. Jedes Movement wird als
 * eigenständiger ``FileUploadResponse`` (eigenes ``file_id``, eigener ``music_xml``-Inhalt)
 * zurückgegeben; die Movements werden **nicht** mehr serverseitig zusammengeführt. Bei
 * einteiligen PDFs enthält ``movements`` genau einen Eintrag. Die ``document_id`` gruppiert die
 * Movements clientseitig (opakes Label).
 */
export type PdfUploadResponse = {
    /**
     * Document Id
     */
    document_id: string;
    /**
     * Filename
     */
    filename: string;
    /**
     * Movements
     */
    movements: Array<FileUploadResponse>;
    /**
     * Status
     */
    status?: string;
    /**
     * Message
     */
    message?: string | null;
};

/**
 * RebarRequest
 *
 * Request für „Taktart ändern + Noten neu aufteilen" (``POST /api/rebar``).
 */
export type RebarRequest = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Beats
     *
     * Zähler der neuen Taktart.
     */
    beats: number;
    beat_type: BeatTypeEnum;
    /**
     * Start Measure Index
     *
     * 0-basierter Takt-**Index** (nicht Taktnummer), ab dem die neue Taktart bis zum Stückende gilt. Die Takte davor bleiben unverändert.
     */
    start_measure_index?: number;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, der neu aufgeteilt wird (inhaltsbasiert, stateless).
     */
    music_xml: string;
};

/**
 * RenderRequest
 *
 * Request to render raw MusicXML content to a binary artifact (stateless).
 *
 * Erzeugt PDF/PNG/MIDI direkt aus dem übergebenen ``music_xml`` (Temp-Dir, sofort wieder
 * gelöscht) – ohne serverseitige Ablage/``conversion_id``. Ersetzt die früheren
 * ``GET /api/download/{conversion_id}/{format}``-Downloads.
 */
export type RenderRequest = {
    /**
     * Music Xml
     */
    music_xml: string;
    /**
     * Format
     *
     * pdf | png | midi | mid
     */
    format?: string;
};

/**
 * SettingsResponse
 *
 * Current settings response.
 */
export type SettingsResponse = {
    settings?: ConversionSettings;
};

/**
 * TranspositionRequest
 *
 * Request to transpose a file.
 */
export type TranspositionRequest = {
    /**
     * File Id
     */
    file_id: string;
    /**
     * Semitones
     *
     * Interval to transpose in semitones
     */
    semitones?: number | null;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, der transponiert wird (inhaltsbasiert, reihenfolgeunabhängig). Fehlt er, wird auf den Original-Upload zurückgegriffen.
     */
    music_xml?: string | null;
    /**
     * Part Indices
     *
     * Optional: 0-basierte Indizes der zu transponierenden Parts (score.parts). None/leer = ganzer Score (bisheriges Verhalten). Dediziert – NICHT identisch mit ConversionSettings.selected_parts (Convert-Scope).
     */
    part_indices?: Array<number> | null;
};

/**
 * TranspositionResult
 */
export type TranspositionResult = {
    /**
     * Interval
     */
    interval: string;
    /**
     * Semitones
     */
    semitones: number;
    /**
     * Target Key
     */
    target_key: string;
    analysis: UnmappableAnalysis;
    /**
     * Quality
     */
    quality: string;
    /**
     * Octave
     */
    octave: number;
    /**
     * Mode Change
     */
    mode_change?: string | null;
    /**
     * Target Mode
     */
    target_mode?: string | null;
};

/**
 * TranspositionSuggestionsResult
 *
 * Dediziertes Ergebnis der Transpositionssuche.
 */
export type TranspositionSuggestionsResult = {
    /**
     * Suggestions
     */
    suggestions?: Array<TranspositionResult>;
    suggestion?: TranspositionResult | null;
    /**
     * Mode Change Suggestions
     *
     * Variantwechsel-Vorschläge (Dur↔Moll, mode_change='parallel') – eigene Liste, damit `semitones` innerhalb jeder Liste eindeutig bleibt (Frontend-`:key`).
     */
    mode_change_suggestions?: Array<TranspositionResult>;
};

/**
 * UnmappableAnalysis
 */
export type UnmappableAnalysis = {
    /**
     * Unmappable
     */
    unmappable: Array<string>;
    /**
     * Total Notes
     */
    total_notes: number;
    /**
     * Unmappable Count
     */
    unmappable_count: number;
    /**
     * Percentage
     */
    percentage: number;
    /**
     * Unmappable Occurrences
     */
    unmappable_occurrences: {
        [key: string]: number;
    };
    /**
     * Total Occurrences
     */
    total_occurrences: number;
    /**
     * Unmappable Occurrences Count
     */
    unmappable_occurrences_count: number;
};

/**
 * ValidationError
 */
export type ValidationError = {
    /**
     * Location
     */
    loc: Array<string | number>;
    /**
     * Message
     */
    msg: string;
    /**
     * Error Type
     */
    type: string;
    /**
     * Input
     */
    input?: unknown;
    /**
     * Context
     */
    ctx?: {
        [key: string]: unknown;
    };
};

/**
 * VoiceMergeRequest
 *
 * Request to merge voices/parts of a file (eigenständiger Input-Schritt).
 */
export type VoiceMergeRequest = {
    /**
     * File Id
     */
    file_id: string;
    settings: VoiceMergeSettings;
    /**
     * Music Xml
     *
     * Der aktuell angezeigte MusicXML-Inhalt, auf den das Zusammenführen angewendet wird (inhaltsbasiert, reihenfolgeunabhängig). Die file_id dient nur als Ablageort für die Ausgabe (Download-/MIDI-URLs).
     */
    music_xml: string;
};

/**
 * VoiceMergeSettings
 *
 * Settings for merging voices/parts as a standalone input transformation.
 *
 * Wird vom Endpunkt ``POST /api/merge-voices`` als Request-Modell genutzt und auf den
 * übergebenen MusicXML-Inhalt angewendet (inhaltsbasiert, reihenfolgeunabhängig) –
 * analog zu Transposition und diatonischer Verschiebung. ``merge_parts`` ist die ehemalige
 * ``COMBINE``-Logik (2. Stimme/Part als Akkorde in die erste zusammenführen); die beiden
 * Normalisierungen ``remove_bass_parts``/``flatten_voices`` laufen weiterhin idempotent in
 * der Konvertierungspipeline und werden hier zusätzlich explizit angeboten.
 */
export type VoiceMergeSettings = {
    /**
     * Enabled
     */
    enabled?: boolean;
    /**
     * Merge Parts
     */
    merge_parts?: boolean;
    /**
     * Remove Bass Parts
     */
    remove_bass_parts?: boolean;
    /**
     * Flatten Voices
     */
    flatten_voices?: boolean;
};

/**
 * Kind
 */
export enum KindEnum {
    Bass = 'bass',
    Chord = 'chord'
}

/**
 * Operation
 */
export enum OperationEnum {
    AddShiftedNotes = 'add_shifted_notes',
    ReduceTriadToDyad = 'reduce_triad_to_dyad',
    RemoveHighestNote = 'remove_highest_note',
    RemoveLowestNote = 'remove_lowest_note'
}

/**
 * Triad Reduction
 */
export enum TriadReductionEnum {
    LowMid = 'low_mid',
    LowHigh = 'low_high',
    MidHigh = 'mid_high'
}

/**
 * Target Mode
 */
export enum TargetModeEnum {
    Major = 'major',
    Minor = 'minor'
}

/**
 * Beat Type
 *
 * Nenner der neuen Taktart.
 */
export enum BeatTypeEnum {
    _1 = 1,
    _2 = 2,
    _4 = 4,
    _8 = 8,
    _16 = 16,
    _32 = 32
}

export type GetCachesApiCacheGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/cache';
};

export type GetCachesApiCacheGetResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type UploadFileApiUploadPostData = {
    body: BodyUploadFileApiUploadPost;
    path?: never;
    query?: never;
    url: '/api/upload';
};

export type UploadFileApiUploadPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UploadFileApiUploadPostError = UploadFileApiUploadPostErrors[keyof UploadFileApiUploadPostErrors];

export type UploadFileApiUploadPostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type UploadFileApiUploadPostResponse = UploadFileApiUploadPostResponses[keyof UploadFileApiUploadPostResponses];

export type UploadPdfApiUploadPdfPostData = {
    body: BodyUploadPdfApiUploadPdfPost;
    path?: never;
    query?: never;
    url: '/api/upload-pdf';
};

export type UploadPdfApiUploadPdfPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type UploadPdfApiUploadPdfPostError = UploadPdfApiUploadPdfPostErrors[keyof UploadPdfApiUploadPdfPostErrors];

export type UploadPdfApiUploadPdfPostResponses = {
    /**
     * Successful Response
     */
    200: PdfUploadResponse;
};

export type UploadPdfApiUploadPdfPostResponse = UploadPdfApiUploadPdfPostResponses[keyof UploadPdfApiUploadPdfPostResponses];

export type AnalyzeFileApiAnalyzePostData = {
    body: AnalysisRequest;
    path?: never;
    query?: never;
    url: '/api/analyze';
};

export type AnalyzeFileApiAnalyzePostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type AnalyzeFileApiAnalyzePostError = AnalyzeFileApiAnalyzePostErrors[keyof AnalyzeFileApiAnalyzePostErrors];

export type AnalyzeFileApiAnalyzePostResponses = {
    /**
     * Successful Response
     */
    200: AnalysisResult;
};

export type AnalyzeFileApiAnalyzePostResponse = AnalyzeFileApiAnalyzePostResponses[keyof AnalyzeFileApiAnalyzePostResponses];

export type TranspositionSuggestionsApiTranspositionSuggestionsPostData = {
    body: AnalysisRequest;
    path?: never;
    query?: never;
    url: '/api/transposition-suggestions';
};

export type TranspositionSuggestionsApiTranspositionSuggestionsPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type TranspositionSuggestionsApiTranspositionSuggestionsPostError = TranspositionSuggestionsApiTranspositionSuggestionsPostErrors[keyof TranspositionSuggestionsApiTranspositionSuggestionsPostErrors];

export type TranspositionSuggestionsApiTranspositionSuggestionsPostResponses = {
    /**
     * Successful Response
     */
    200: TranspositionSuggestionsResult;
};

export type TranspositionSuggestionsApiTranspositionSuggestionsPostResponse = TranspositionSuggestionsApiTranspositionSuggestionsPostResponses[keyof TranspositionSuggestionsApiTranspositionSuggestionsPostResponses];

export type TransposeFileApiTransposePostData = {
    body: TranspositionRequest;
    path?: never;
    query?: never;
    url: '/api/transpose';
};

export type TransposeFileApiTransposePostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type TransposeFileApiTransposePostError = TransposeFileApiTransposePostErrors[keyof TransposeFileApiTransposePostErrors];

export type TransposeFileApiTransposePostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type TransposeFileApiTransposePostResponse = TransposeFileApiTransposePostResponses[keyof TransposeFileApiTransposePostResponses];

export type ModeChangeFileApiModeChangePostData = {
    body: ModeChangeRequest;
    path?: never;
    query?: never;
    url: '/api/mode-change';
};

export type ModeChangeFileApiModeChangePostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ModeChangeFileApiModeChangePostError = ModeChangeFileApiModeChangePostErrors[keyof ModeChangeFileApiModeChangePostErrors];

export type ModeChangeFileApiModeChangePostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type ModeChangeFileApiModeChangePostResponse = ModeChangeFileApiModeChangePostResponses[keyof ModeChangeFileApiModeChangePostResponses];

export type RebarFileApiRebarPostData = {
    body: RebarRequest;
    path?: never;
    query?: never;
    url: '/api/rebar';
};

export type RebarFileApiRebarPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RebarFileApiRebarPostError = RebarFileApiRebarPostErrors[keyof RebarFileApiRebarPostErrors];

export type RebarFileApiRebarPostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type RebarFileApiRebarPostResponse = RebarFileApiRebarPostResponses[keyof RebarFileApiRebarPostResponses];

export type DiatonicShiftFileApiDiatonicShiftPostData = {
    body: DiatonicShiftRequest;
    path?: never;
    query?: never;
    url: '/api/diatonic-shift';
};

export type DiatonicShiftFileApiDiatonicShiftPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type DiatonicShiftFileApiDiatonicShiftPostError = DiatonicShiftFileApiDiatonicShiftPostErrors[keyof DiatonicShiftFileApiDiatonicShiftPostErrors];

export type DiatonicShiftFileApiDiatonicShiftPostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type DiatonicShiftFileApiDiatonicShiftPostResponse = DiatonicShiftFileApiDiatonicShiftPostResponses[keyof DiatonicShiftFileApiDiatonicShiftPostResponses];

export type MergeVoicesFileApiMergeVoicesPostData = {
    body: VoiceMergeRequest;
    path?: never;
    query?: never;
    url: '/api/merge-voices';
};

export type MergeVoicesFileApiMergeVoicesPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type MergeVoicesFileApiMergeVoicesPostError = MergeVoicesFileApiMergeVoicesPostErrors[keyof MergeVoicesFileApiMergeVoicesPostErrors];

export type MergeVoicesFileApiMergeVoicesPostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type MergeVoicesFileApiMergeVoicesPostResponse = MergeVoicesFileApiMergeVoicesPostResponses[keyof MergeVoicesFileApiMergeVoicesPostResponses];

export type BassPartFileApiBassPartPostData = {
    body: BassPreviewRequest;
    path?: never;
    query?: never;
    url: '/api/bass-part';
};

export type BassPartFileApiBassPartPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type BassPartFileApiBassPartPostError = BassPartFileApiBassPartPostErrors[keyof BassPartFileApiBassPartPostErrors];

export type BassPartFileApiBassPartPostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type BassPartFileApiBassPartPostResponse = BassPartFileApiBassPartPostResponses[keyof BassPartFileApiBassPartPostResponses];

export type ChordInferenceFileApiChordInferencePostData = {
    body: ChordInferenceRequest;
    path?: never;
    query?: never;
    url: '/api/chord-inference';
};

export type ChordInferenceFileApiChordInferencePostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ChordInferenceFileApiChordInferencePostError = ChordInferenceFileApiChordInferencePostErrors[keyof ChordInferenceFileApiChordInferencePostErrors];

export type ChordInferenceFileApiChordInferencePostResponses = {
    /**
     * Successful Response
     */
    200: FileUploadResponse;
};

export type ChordInferenceFileApiChordInferencePostResponse = ChordInferenceFileApiChordInferencePostResponses[keyof ChordInferenceFileApiChordInferencePostResponses];

export type GenerateMidiFromXmlApiMidiPostData = {
    body: MidiFromXmlRequest;
    path?: never;
    query?: never;
    url: '/api/midi';
};

export type GenerateMidiFromXmlApiMidiPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type GenerateMidiFromXmlApiMidiPostError = GenerateMidiFromXmlApiMidiPostErrors[keyof GenerateMidiFromXmlApiMidiPostErrors];

export type GenerateMidiFromXmlApiMidiPostResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type NoteGripsApiNoteGripsPostData = {
    body: NoteGripsRequest;
    path?: never;
    query?: never;
    url: '/api/note-grips';
};

export type NoteGripsApiNoteGripsPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type NoteGripsApiNoteGripsPostError = NoteGripsApiNoteGripsPostErrors[keyof NoteGripsApiNoteGripsPostErrors];

export type NoteGripsApiNoteGripsPostResponses = {
    /**
     * Successful Response
     */
    200: NoteGripsResponse;
};

export type NoteGripsApiNoteGripsPostResponse = NoteGripsApiNoteGripsPostResponses[keyof NoteGripsApiNoteGripsPostResponses];

export type ChordGripsApiChordGripsPostData = {
    body: ChordGripsRequest;
    path?: never;
    query?: never;
    url: '/api/chord-grips';
};

export type ChordGripsApiChordGripsPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ChordGripsApiChordGripsPostError = ChordGripsApiChordGripsPostErrors[keyof ChordGripsApiChordGripsPostErrors];

export type ChordGripsApiChordGripsPostResponses = {
    /**
     * Successful Response
     */
    200: ChordGripsResponse;
};

export type ChordGripsApiChordGripsPostResponse = ChordGripsApiChordGripsPostResponses[keyof ChordGripsApiChordGripsPostResponses];

export type BassButtonTonesApiBassButtonTonesPostData = {
    body: BassButtonTonesRequest;
    path?: never;
    query?: never;
    url: '/api/bass-button-tones';
};

export type BassButtonTonesApiBassButtonTonesPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type BassButtonTonesApiBassButtonTonesPostError = BassButtonTonesApiBassButtonTonesPostErrors[keyof BassButtonTonesApiBassButtonTonesPostErrors];

export type BassButtonTonesApiBassButtonTonesPostResponses = {
    /**
     * Successful Response
     */
    200: BassButtonTonesResponse;
};

export type BassButtonTonesApiBassButtonTonesPostResponse = BassButtonTonesApiBassButtonTonesPostResponses[keyof BassButtonTonesApiBassButtonTonesPostResponses];

export type RenderMusicXmlApiRenderPostData = {
    body: RenderRequest;
    path?: never;
    query?: never;
    url: '/api/render';
};

export type RenderMusicXmlApiRenderPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type RenderMusicXmlApiRenderPostError = RenderMusicXmlApiRenderPostErrors[keyof RenderMusicXmlApiRenderPostErrors];

export type RenderMusicXmlApiRenderPostResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type ConvertToGriffschriftApiConvertPostData = {
    body: ConversionRequest;
    path?: never;
    query?: never;
    url: '/api/convert';
};

export type ConvertToGriffschriftApiConvertPostErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ConvertToGriffschriftApiConvertPostError = ConvertToGriffschriftApiConvertPostErrors[keyof ConvertToGriffschriftApiConvertPostErrors];

export type ConvertToGriffschriftApiConvertPostResponses = {
    /**
     * Successful Response
     */
    200: ConversionStatusStore;
};

export type ConvertToGriffschriftApiConvertPostResponse = ConvertToGriffschriftApiConvertPostResponses[keyof ConvertToGriffschriftApiConvertPostResponses];

export type ConvertVariantsData = {
    body: ConvertVariantsRequest;
    path?: never;
    query?: never;
    url: '/api/convert/variants';
};

export type ConvertVariantsErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ConvertVariantsError = ConvertVariantsErrors[keyof ConvertVariantsErrors];

export type ConvertVariantsResponses = {
    /**
     * Successful Response
     */
    200: ConvertVariantsResponse;
};

export type ConvertVariantsResponse2 = ConvertVariantsResponses[keyof ConvertVariantsResponses];

export type ConvertAssembleData = {
    body: AssembleRequest;
    path?: never;
    query?: never;
    url: '/api/convert/assemble';
};

export type ConvertAssembleErrors = {
    /**
     * Validation Error
     */
    422: HttpValidationError;
};

export type ConvertAssembleError = ConvertAssembleErrors[keyof ConvertAssembleErrors];

export type ConvertAssembleResponses = {
    /**
     * Successful Response
     */
    200: ConversionStatusStore;
};

export type ConvertAssembleResponse = ConvertAssembleResponses[keyof ConvertAssembleResponses];

export type GetSettingsApiSettingsGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/settings';
};

export type GetSettingsApiSettingsGetResponses = {
    /**
     * Successful Response
     */
    200: SettingsResponse;
};

export type GetSettingsApiSettingsGetResponse = GetSettingsApiSettingsGetResponses[keyof GetSettingsApiSettingsGetResponses];

export type ReadRootGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/';
};

export type ReadRootGetResponses = {
    /**
     * Successful Response
     */
    200: string;
};

export type ReadRootGetResponse = ReadRootGetResponses[keyof ReadRootGetResponses];

export type HealthCheckHealthGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/health';
};

export type HealthCheckHealthGetResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};

export type GetOpenapiSpecApiOpenapiGetData = {
    body?: never;
    path?: never;
    query?: never;
    url: '/api/openapi';
};

export type GetOpenapiSpecApiOpenapiGetResponses = {
    /**
     * Successful Response
     */
    200: unknown;
};
